/* eslint-disable react/no-multi-comp */
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { TranslationService } from '@/utils/services/TranslationService';

import useUser from '@/utils/hooks/swr/useUser';
//Hooks
import useUserMarketing from '@/utils/hooks/swr/useUserMarketing';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';

import { SIGN_UP_URL_PATHNAME } from '@/lib/routes/privateRoutes';
// Types
import type { SubStepType } from '@/lib/signup/userInfos/subSteps';
// Lib
import { subSteps } from '@/lib/signup/userInfos/subSteps';

import InfoBanner from '@/components/base/InfoBanner/CompoundBanner';
//components
import Loader from '@/components/base/Loader';
import LayoutSignUpLogged from '@/components/layouts/LayoutSignUpLogged';
import SideContentVisual from '@/components/pages/signup/SideContentVisual';
import UserInfosForm from '@/components/pages/signup/userInfos/UserInfosForm';
import { Trans } from '@/components/tools/translation';

//utils
import { SIGN_UP } from '@/routes/privateRoutes';

type PageMainContentProps = {
    currentSubStep: SubStepType;
    setCurrentSubStep: (subStep: SubStepType) => void;
};

const PageMainContent = ({ currentSubStep, setCurrentSubStep }: PageMainContentProps) => {
    const { t: tErrors } = useTranslation('errors');

    const { userMarket, userMarketError, mutateUserMarket, isLoading } = useUserMarketing();

    if (userMarketError) {
        return (
            <InfoBanner>
                <InfoBanner.Icon />
                <InfoBanner.Content
                    title={tErrors('error_occured')}
                    message={
                        <Trans
                            i18nKey="errors:error_server_occurred"
                            components={{
                                tag0: (
                                    <button
                                        key="error"
                                        className="text-icoRed underline"
                                        onClick={() => {
                                            mutateUserMarket(null, { revalidate: true });
                                        }}
                                    />
                                )
                            }}
                        />
                    }
                />
            </InfoBanner>
        );
    }

    if (isLoading || !userMarket) {
        return (
            <div className="flex flex-1 items-center justify-center">
                <Loader className="h-7 w-7 text-icoBlue" />
            </div>
        );
    }

    return <UserInfosForm currentSubStep={currentSubStep} setCurrentSubStep={setCurrentSubStep} />;
};

const PageContent = () => {
    const { t: tSignupFunnel } = useTranslation('signup_funnel');
    const router = useRouter();
    const { userMarket } = useUserMarketing();
    const { user } = useUser();
    const [currentSubStep, setCurrentSubStep] = useState(subSteps[0]);
    const hasInitializedSubStep = useRef(false);

    useEffect(() => {
        if (window?.dataLayer && !user?.first_name) {
            window.dataLayer.push({
                event: 'lead_created'
            });
        }
    }, []);

    useEffect(() => {
        // Prefetch the next step : connect social profile page
        router.prefetch(SIGN_UP['USER_SET_UP']);
    }, [router]);

    // When user has already completed step 2 (user_set_up) + make sure all substeps are filled, open on subStep 3 by default — only on first load so they can still use Back to subSteps 0–2
    useEffect(() => {
        if (userMarket) {
            if (userMarket.sign_up_step === 'user_set_up' && userMarket.primary_goal !== null) {
                hasInitializedSubStep.current = true;
                setCurrentSubStep(subSteps[2]);
            } else {
                setCurrentSubStep(subSteps[0]);
            }
        }
    }, [userMarket]);

    return (
        <>
            <LayoutSignUpLogged.MainContent
                title={tSignupFunnel(currentSubStep?.title)}
                subtitle={tSignupFunnel(currentSubStep?.subtitle)}
                headerChildren={
                    <span className="absolute top-0 rounded-full border border-icoBlue-200 px-5 py-2 text-13 text-icoBlue">
                        {currentSubStep.id}/{subSteps.length}
                    </span>
                }>
                <PageMainContent
                    currentSubStep={currentSubStep}
                    setCurrentSubStep={setCurrentSubStep}
                />
            </LayoutSignUpLogged.MainContent>
            <LayoutSignUpLogged.SideContent>
                <SideContentVisual
                    src={currentSubStep?.sideContentVisual.src}
                    width={currentSubStep?.sideContentVisual.width}
                    height={currentSubStep?.sideContentVisual.height}
                    className={classNames({
                        'bg-icoSignUpGradient2 pl-24': currentSubStep.id === 1,
                        'bg-icoSignUpGradient3 pl-16': currentSubStep.id === 2,
                        'bg-icoSignUpGradient4 pl-14': currentSubStep.id === 3
                    })}
                />
            </LayoutSignUpLogged.SideContent>
        </>
    );
};

const UserInfos = () => {
    return (
        <LayoutSignUpLogged>
            <PageContent />
        </LayoutSignUpLogged>
    );
};

export const getStaticProps = TranslationService.getTranslationMessagesFromRoute(
    SIGN_UP_URL_PATHNAME.USER_CREATED
);

export default UserInfos;
